import { z } from 'zod';
import { catalogueStatusSchema, hvacStateSchema, powertrainTypeSchema, routeTypeSchema, ruleSeveritySchema, seasonSchema, testTypeSchema, trafficLevelSchema, } from '../../common/schemas/enum.schemas.js';
import { idParamSchema, uuidSchema } from '../../common/schemas/id.schemas.js';
import { paginatedSchema, paginationInputSchema, sortInputSchema } from '../../common/schemas/pagination.schemas.js';
/**
 * The operator set the validity engine implements (plan §6). `equal_field` is the reference-comparing
 * sibling of `equal`: it holds a reading against *another* field of the same run rather than a constant —
 * a run's recorded traffic against the traffic its scenario prescribes, for instance.
 */
export const ruleOperatorSchema = z.enum(['between', 'lte', 'gte', 'equal', 'equal_field', 'in', 'not_null', 'min_pct_of']);
/**
 * A dotted path into the run's evaluation context — `measurements.ambient_temp_avg_c`,
 * `conditions.fuel_grade`, `computed.distance_pct_of_route`. The group is camelCase-free on purpose:
 * the leaf names are the database columns the field refers to.
 */
export const ruleFieldPathSchema = z
    .string()
    .trim()
    .regex(/^(run|conditions|measurements|computed|vehicle|scenario|route|protocol)\.[a-z0-9_]+$/, 'Use a path like measurements.ambient_temp_avg_c');
/**
 * Narrows a rule to the runs it is about. Every listed dimension has to match, so
 * `{ seasons: ['SUMMER'] }` is the summer temperature envelope and nothing else. A rule that does not
 * apply records no outcome at all rather than a free PASS.
 */
export const ruleAppliesToSchema = z.object({
    testTypes: z.array(testTypeSchema).min(1).optional(),
    seasons: z.array(seasonSchema).min(1).optional(),
    routeTypes: z.array(routeTypeSchema).min(1).optional(),
    hvacStates: z.array(hvacStateSchema).min(1).optional(),
    powertrainTypes: z.array(powertrainTypeSchema).min(1).optional(),
    /**
     * The traffic the *scenario* prescribes, not the traffic the run met. Listing every level is therefore
     * the way to say "only scenarios that specify traffic at all" — which is exactly how the seeded
     * `traffic_matches_scenario` rule stays inapplicable to the scenarios that leave traffic unspecified.
     */
    scenarioTrafficLevels: z.array(trafficLevelSchema).min(1).optional(),
});
const ruleValueSchema = z.union([z.number(), z.string(), z.boolean()]);
/**
 * Tolerant read shape: unknown keys are dropped rather than rejected, so a rule row written by a later
 * version of the platform still lists. Writes go through `validityRuleDefinitionInputSchema`.
 */
export const validityRuleDefinitionSchema = z.object({
    field: ruleFieldPathSchema,
    operator: ruleOperatorSchema,
    min: z.number().optional(),
    max: z.number().optional(),
    value: ruleValueSchema.optional(),
    values: z
        .array(z.union([z.number(), z.string()]))
        .min(1)
        .optional(),
    /** Another field path, e.g. `route.distance_km` for `min_pct_of`. */
    reference: z.string().trim().optional(),
    unit: z.string().trim().max(16).optional(),
    appliesTo: ruleAppliesToSchema.optional(),
});
/** Each operator needs its own parameters; an incomplete rule would silently fail every run. */
const checkOperatorParams = (definition, ctx) => {
    const require = (present, path, message) => {
        if (!present) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
        }
    };
    switch (definition.operator) {
        case 'between':
            require(typeof definition.min === 'number', 'min', 'A between rule needs a minimum');
            require(typeof definition.max === 'number', 'max', 'A between rule needs a maximum');
            if (typeof definition.min === 'number' && typeof definition.max === 'number' && definition.min > definition.max) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['max'], message: 'The maximum has to be at least the minimum' });
            }
            return;
        case 'lte':
        case 'gte':
            require(typeof definition.value === 'number', 'value', `A ${definition.operator} rule needs a numeric value`);
            return;
        case 'equal':
            require(definition.value !== undefined, 'value', 'An equal rule needs a value to compare against');
            return;
        case 'equal_field': {
            const hasReference = typeof definition.reference === 'string' && definition.reference !== '';
            require(hasReference, 'reference', 'An equal_field rule needs the field path it compares against as its reference');
            return;
        }
        case 'in':
            require(Array.isArray(definition.values) && definition.values.length > 0, 'values', 'An in rule needs at least one accepted value');
            return;
        case 'min_pct_of':
            require(typeof definition.value === 'number', 'value', 'A min_pct_of rule needs the percentage as its value');
            require(typeof definition.reference === 'string' && definition.reference !== '', 'reference', 'A min_pct_of rule needs a reference field path');
            return;
        case 'not_null':
            return;
    }
};
export const validityRuleDefinitionInputSchema = validityRuleDefinitionSchema.strict().superRefine(checkOperatorParams);
export const validityRuleSchema = z.object({
    id: uuidSchema,
    protocolId: uuidSchema,
    protocolCode: z.string().min(1),
    protocolVersion: z.number().int().min(1),
    ruleKey: z.string().min(1),
    definition: validityRuleDefinitionSchema,
    severity: ruleSeveritySchema,
    status: catalogueStatusSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const VALIDITY_RULE_SORT_FIELDS = ['ruleKey', 'severity', 'createdAt'];
export const validityRuleSortBySchema = z.enum(VALIDITY_RULE_SORT_FIELDS);
export const listValidityRulesInputSchema = z.object({
    ...paginationInputSchema.shape,
    ...sortInputSchema.shape,
    sortBy: validityRuleSortBySchema.default('ruleKey'),
    protocolId: uuidSchema.optional(),
    severity: ruleSeveritySchema.optional(),
    status: catalogueStatusSchema.optional(),
});
export const listValidityRulesOutputSchema = paginatedSchema(validityRuleSchema);
/**
 * The rule key and its protocol are its identity — an update replaces the definition, severity and
 * status of an existing keyed row, so historical runs stay traceable to a rule that still exists.
 */
export const updateValidityRuleInputSchema = z.object({
    ...idParamSchema.shape,
    definition: validityRuleDefinitionInputSchema,
    severity: ruleSeveritySchema,
    status: catalogueStatusSchema,
});
/**
 * Comparison eligibility (plan §6). `same_route` is the route-aware sibling of `equal`: it holds the runs
 * against the same *road* — the route code, so two surveyed versions of one circuit still count as one
 * route — while printing the route's name, and it treats an idle test's missing route as correct rather
 * than absent.
 */
export const compatibilityOperatorSchema = z.enum(['equal', 'same_route', 'max_diff']);
export const compatibilityOnFailSchema = z.enum(['BLOCK', 'WARN']);
export const compatibilityDefinitionSchema = z.object({
    operator: compatibilityOperatorSchema,
    /** The tolerance for `max_diff` — 5 °C of ambient spread, 50 kg of payload spread. */
    value: z.number().optional(),
    unit: z.string().trim().max(16).optional(),
    onFail: compatibilityOnFailSchema,
});
export const compatibilityDefinitionInputSchema = compatibilityDefinitionSchema.strict().superRefine((definition, ctx) => {
    if (definition.operator === 'max_diff' && typeof definition.value !== 'number') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['value'], message: 'A max_diff check needs its tolerance as a numeric value' });
    }
});
export const compatibilityConfigSchema = z.object({
    id: uuidSchema,
    protocolId: uuidSchema,
    protocolCode: z.string().min(1),
    protocolVersion: z.number().int().min(1),
    key: z.string().min(1),
    definition: compatibilityDefinitionSchema,
    status: catalogueStatusSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const COMPATIBILITY_SORT_FIELDS = ['key', 'createdAt'];
export const compatibilitySortBySchema = z.enum(COMPATIBILITY_SORT_FIELDS);
export const listCompatibilityInputSchema = z.object({
    ...paginationInputSchema.shape,
    ...sortInputSchema.shape,
    sortBy: compatibilitySortBySchema.default('key'),
    protocolId: uuidSchema.optional(),
    status: catalogueStatusSchema.optional(),
});
export const listCompatibilityOutputSchema = paginatedSchema(compatibilityConfigSchema);
export const updateCompatibilityInputSchema = z.object({
    ...idParamSchema.shape,
    definition: compatibilityDefinitionInputSchema,
    status: catalogueStatusSchema,
});
//# sourceMappingURL=config.schemas.js.map